import { camelizeKeys } from "humps";
import { Card } from "PFComponents/card";
import { SelectV2 } from "PFComponents/select_v2";
import Toggle from "PFComponents/toggle/toggle";
import { Typography } from "PFComponents/typography";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { fetchAutocompleteCustomValues } from "PFCore/services/autocomplete/fetch_autocomplete_custom_values";
import { ChangeRequest, PureCustomValueCamelCased } from "PFTypes";
import { useTranslation } from "react-i18next";

import css from "../change_request_page.module.scss";
import { DisabledItems, MergeData, SetDisabledItems } from "../change_request_page.types";
import { Input } from "./input";

type AboutSectionProps = {
  data: ChangeRequest["data"];
  mergeData: MergeData;
  disabledItems: DisabledItems;
  setDisabledItems: SetDisabledItems;
  errors: any;
};

export const AboutSection = ({
  data,
  mergeData,
  disabledItems,
  setDisabledItems,
  errors
}: AboutSectionProps) => {
  const { customTypes } = useCustomTypes();
  const { t } = useTranslation("changeRequests");

  if (
    !data ||
    (typeof data.first_name !== "string" &&
      typeof data.last_name !== "string" &&
      typeof data.summary !== "string" &&
      !data.custom_fields?.length)
  ) {
    return null;
  }

  const commonProps = {
    data,
    mergeData,
    disabledItems,
    setDisabledItems
  };

  return (
    <Card qaId="ChangeRequest.About">
      <Typography withMargin variant="h2" className={css.header}>
        {t("parts.about")}
      </Typography>
      <div className={css.form}>
        <Input
          {...commonProps}
          prop="first_name"
          label={t("parts.inputs.firstName")}
          error={errors.firstName}
        />
        <Input {...commonProps} prop="last_name" label={t("parts.inputs.lastName")} error={errors.lastName} />
        <Input
          {...commonProps}
          prop="summary"
          label={t("parts.inputs.shortBio")}
          inputType="textarea"
          error={errors.summary}
        />

        {data?.custom_fields?.map((customField) => {
          const type = customTypes.find(({ name }) => name === customField.type.name);

          if (!type) {
            return null;
          }

          return (
            <div key={type.name} className={css.inputWrapper} data-qa-id={`ChangeRequest.About.${type.name}`}>
              <SelectV2<PureCustomValueCamelCased>
                cacheTime={0}
                key={type.name}
                label={type.display_as}
                disabled={disabledItems.customFields[type.name]}
                tip={type.description}
                {...(type.kind === "multiple"
                  ? {
                      multiple: true as const,
                      value: customField.values.map((it) => it.global_id ?? it.value)
                    }
                  : { value: customField.values[0]?.global_id ?? customField.values[0]?.value })}
                error={errors?.["custom_fields"]?.find((item) => item[type.name])?.[type.name]}
                fetchOptions={({ searchTerm, ids }) => {
                  if (ids) {
                    return Promise.resolve({
                      entries: customField.values.map((item) => ({
                        id: item.global_id ?? item.value,
                        value: item.value,
                        original: camelizeKeys(item) as PureCustomValueCamelCased
                      })),
                      meta: { total: customField.values.length }
                    });
                  }
                  return fetchAutocompleteCustomValues({
                    term: searchTerm ?? "",
                    type: customField.type.name
                  }).then((entries) => ({
                    entries: entries.map((item) => ({
                      id: item.globalId ?? String(item.text),
                      value: String(item.text),
                      original: item
                    })),
                    meta: { total: entries.length }
                  }));
                }}
                onChange={(options) => {
                  const items = Array.isArray(options) ? options : [options];
                  const updatedValues = items.map((opt) => ({
                    value: opt.value,
                    global_id: opt.original.globalId ?? undefined
                  }));
                  const updatedCustomFields = data.custom_fields?.map((cf) =>
                    cf.type.name === customField.type.name ? { ...cf, values: updatedValues } : cf
                  );
                  mergeData({ custom_fields: updatedCustomFields });
                }}
              />
              <div>
                <Toggle
                  checked={!disabledItems.customFields[type.name]}
                  onChange={(val) => {
                    setDisabledItems((prev) => ({
                      ...prev,
                      customFields: { ...prev.customFields, [type.name]: !val }
                    }));
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
